import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { ApiError, client, unwrap } from "@/api/client";
import type { components } from "@/api/schema";
import { t } from "@/i18n";

export type Similarities = components["schemas"]["Similarities"];
export type Keyword = components["schemas"]["Keyword"];
export type CompanyPage = components["schemas"]["CompanyPage"];
export type ReceptionCompany = components["schemas"]["ReceptionCompany"];
export type CompanyMatch = components["schemas"]["CompanyMatch"];
export type ContactMatch = components["schemas"]["ContactMatch"];
export type BranchMatch = components["schemas"]["BranchMatch"];
export type CompanyFile = components["schemas"]["CompanyFile"];
export type InboundCall = components["schemas"]["InboundCallRequest"];
export type InboundCallResult = components["schemas"]["InboundCallResult"];

/** What has been typed so far, as the search takes it. */
export type SimilarityQuery = {
  company_name: string;
  contact_name: string;
  phone: string;
  email: string;
};

/**
 * Who in the book this call might be from.
 *
 * The previous answer stays on screen while the next one loads: reception is
 * typing with a caller on the line, and four sections that blank out on every
 * keystroke cannot be read. The same goes for turning a page of *Palabras
 * clave*, the one block that has more than one.
 */
export function useSimilarities(
  query: SimilarityQuery,
  keywordsPage: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["reception", "search", query, keywordsPage],
    enabled,
    queryFn: () =>
      unwrap(
        client.GET("/api/reception/search/", {
          params: { query: { ...query, keywords_page: keywordsPage } },
        }),
        t("errors.receptionSearch"),
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * A company's sites and everybody at it, whoever holds the account.
 *
 * Not `useCompanyContacts`: `/api/contacts/` hides a colleague's contacts from
 * everyone but management, reception included, and this screen has to read
 * them to say whether the caller is already in the book.
 */
export function useCompanyFile(id: number, enabled = true) {
  return useQuery({
    queryKey: ["reception", "company", id],
    enabled,
    queryFn: () =>
      unwrap(
        client.GET("/api/reception/companies/{id}/", { params: { path: { id } } }),
        t("errors.receptionCompany"),
      ),
  });
}

/** Who a call can be handed to: the people who sell, still on staff. */
export function useSalesAgents(enabled = true) {
  return useQuery({
    queryKey: ["agents", "sales"],
    enabled,
    queryFn: () =>
      unwrap(
        client.GET("/api/users/agents/", { params: { query: { role: "sales" } } }),
        t("errors.agents"),
      ),
    // Staff changes are an admin action, not something that happens while a
    // call is being typed in.
    staleTime: 5 * 60_000,
  });
}

/**
 * The server refused to create a company because the name is already one.
 *
 * Carries the company it found, which `unwrap` would have thrown away: the
 * screen's answer to this refusal is to point at that row.
 */
export class DuplicateCompanyError extends ApiError {
  constructor(
    message: string,
    readonly company: ReceptionCompany,
  ) {
    super(message, 409);
    this.name = "DuplicateCompanyError";
  }
}

/**
 * Register the call and hand it to a seller.
 *
 * One request and one transaction, whichever of the five situations it is
 * (new company, held or unheld account, contact found or just created): the
 * server reads `company` and `contact` and decides. Offering them as separate
 * calls would put a multi-table invariant in the browser.
 */
export function useRegisterCall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: InboundCall): Promise<InboundCallResult> => {
      const result = await client.POST("/api/reception/calls/", { body });
      if (result.response.status === 409 && result.error) {
        throw new DuplicateCompanyError(result.error.detail, result.error.company);
      }
      return unwrap(Promise.resolve(result), t("errors.registerCall"));
    },
    onSuccess: () => {
      // The call is a lead on the queue and an activity on a timeline, and may
      // have created a company or moved one — with its contacts — to a seller.
      for (const key of ["leads", "companies", "activities", "contacts", "reception"]) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}
